import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, NgZone, OnDestroy, OnInit,
  ViewChild } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs/operators';
import { HttpInterceptorService } from '@ispm/core/services/http-interceptor.service';
import { NOTIFICATION_MESSAGE } from '@ispm-client/modules/aggregator/services/consts';
import { cloneDeep, get, has, set, unset, unionWith } from 'lodash';
import { SearchService } from '@ispm/dynamic-form/services/search.service';
import { TableItemInterface } from '../../../worklist/interfaces';
import { Table, TableHeaderCheckbox } from 'primeng/table';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';
import { StringUtils } from "@ispm/shared/utils/String.utils";

@Component({
  selector: 'ispm-treatment-plans-selection-modal',
  templateUrl: './treatment-plans-selection-modal.component.html',
  styleUrls: ['./treatment-plans-selection-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class TreatmentPlansSelectionModalComponent implements OnInit, AfterViewInit, OnDestroy {

  public payload: {
    url?: string;
    searchField: string,
    searchInput?: { label: string, placeHolderText: string },
    selectAllEnabled: boolean,
    pagination: boolean,
    pageSize: number,
    table: TableItemInterface[],
    alreadySelected?: any[],
    exactMultiSearch: boolean;
  };
  public searchText: string = '';
  public list: any[] = [];
  public values: any[] = [];
  public selectedValues: any[] = [];
  public selectedRows: any[] = [];
  public tunnel$: Subject<any>;
  public callback: (model) => Observable<Response>;
  public iterationRows = 20;
  public sortOrder: number = -1;
  public sortBy: string;
  public loading: boolean = true;
  public length: number;
  public first: number = 0; // first item to show in list
  public sortKeys: any[] = [];
  private selectionUrl: string = ''; // final url, which is used for getting data
  private filterSearch: Subject<string> = new Subject<string>();
  private transform: any; // transform - object, which describes transformation of received data to form {display, code }
  private iterationPage: number = 0; // current page number
  public pageChangeEvent = { first: 0, rows: this.iterationRows, page: this.iterationPage };
  private keepExistedSelection: boolean = false;
  private headerCheckedBoxState: boolean = false;
  private selectedPageNumbers: number[];
  private initState = true;
  private destroyer$: Subject<any> = new Subject<any>();

  @ViewChild(TableHeaderCheckbox) checkbox: TableHeaderCheckbox;
  @ViewChild(Table) table: Table;

  constructor(
    private changeDetector: ChangeDetectorRef,
    private http: HttpInterceptorService,
    private search: SearchService,
    private notificationMessageService: NotificationMessageService,
    private notificationMessage: NOTIFICATION_MESSAGE) {

  }

  ngAfterViewInit(): void {
    this.table.tableService.valueSource$.pipe(take(1)).subscribe(() => this.checkbox.cd.detectChanges());
  }

  public ngOnInit() {

    this.filterSearch.asObservable()
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this.destroyer$.asObservable()),
      )
      .subscribe((value: string) => {
        if (value === '') {
          this.onSearchExecute(value);
        } else {
          this.onSearchChange(value);
        }
      });

    this.tunnel$.asObservable()
      .pipe(
        filter(message => message.emitter !== 'child' && message.value),
        map(message => message.value),
        takeUntil(this.destroyer$.asObservable()),
      ).subscribe((status) => {
        this.callback({ selectedRows: this.selectedRows, status: status})
          .subscribe((model) => {
            this.tunnel$.next({ emitter: 'child', value: model});
          });
      });

    this.transform = this.search.getTransformObject(this.payload.url);
    this.selectionUrl = this.payload.url.split('|')[0];
    this.iterationRows = this.iterationRows || this.payload.pageSize;
    this.filterSearch.next('');
    this.sortBy = this.payload.searchField;
    this.first = 0;
    this.iterationPage = 0;
    this.length = 0;
    this.pageChangeEvent.first = 0;
    this.pageChangeEvent.page = 0;
    this.selectedPageNumbers = [];
  }

  public ngOnDestroy() {
    this.destroyer$.next();
    this.destroyer$.complete();
  }

  public onSearchTextChange(value: string) {
    this.filterSearch.next(value);
  }

  public onSearchChange(searchStr: string) {
    if (!!searchStr && searchStr.length > 0) {
      this.searchText = searchStr;
      this.selectedValues = this.payload.exactMultiSearch ? this.exactMultiSearch(): this.cumulativeMultiSearch();

      this.pageChangeEvent.first = 0;
      this.pageChangeEvent.page = 1;
      this.length = this.selectedValues.length;
      this.list = this.selectedValues.slice(0, (this.iterationRows));
      this.changeDetector.detectChanges();
    }
    else {
      this.searchText = '';
      this.keepExistedSelection = true;
      this.onSearchExecute(this.searchText);
      this.changeDetector.markForCheck();
    }
  }

  private exactMultiSearch(): any[] {
    const countedChunks = StringUtils.getCountedChunks(this.searchText);
    const chunksCount = countedChunks.size;

    return this.values
      .filter(el => {
        for (const key of countedChunks.keys()) {
          countedChunks.set(key, {number: 0, exist: false});
        }
        const weight = {val: 0};
        const text = get(el, this.transform.display, '').toString().toLowerCase();

        const result = StringUtils.isExactCountedIncluded(text, countedChunks);
        let foundChunksNumber: number = 0;

        if (result) {
          for (const value of countedChunks.values()) {
            if (value.exist) {
              weight.val += value.number;
              foundChunksNumber++;
            }
          }
        }
        set(el, 'weight', foundChunksNumber === chunksCount ? weight.val : 0);
        return result;
      })
      .sort((a, b) => b.weight - a.weight)
      .filter(val => val.weight >= chunksCount)
      .map(item => {
        unset(item, 'weight');
        return item;
      });
  }


  private cumulativeMultiSearch() : any[]{
    const searchChunks = StringUtils.getChunks(this.searchText);
    return this.values
      .filter(el => {
        const weight = {val: 0};
        const text = get(el, this.transform.display, '').toString().toLowerCase();

        const result = searchChunks.length > 0 ?
          StringUtils.isIncluded(text, searchChunks, weight) :
          StringUtils.isIncluded(text, [this.searchText], weight);

        if (result && weight.val > 0) {
          set(el, 'weight', weight.val);
        }
        return result;
      })
      .sort((a, b) => b.weight - a.weight)
      .map(item => {
        unset(item, 'weight');
        return item;
      });
  }

  // This function realize search of list item by search text
  public onSearchExecute(searchStr: string) {
    this.loading = true;
    if (!searchStr) {
      searchStr = '';
    }
    this.searchText = searchStr;
    const subst = this.payload.searchField + '=';
    const url = this.selectionUrl.replace(subst, subst + this.searchText);
    this.http.get(`${url}`, {})
      .map(res => res.body)
      .take(1)
      .subscribe(result => {
        if (!result || result.length === 0) {
          this.notificationMessageService.error(this.notificationMessage.CANNOT_GET_TREATMENT_PLANS);
        } else {
          // create list of combo-boxes with 'display' field of resources
          if (Array.isArray(result)) {
            this.values = [];
            this.pageChangeEvent.first = 0;
            this.pageChangeEvent.page = 1;
            this.length = result.length;
            result.forEach(el => {
              if (!!this.transform && has(this.transform, 'display') && has(this.transform, 'code')) {
                el = {...el, display: get(el, this.transform.display), code: get(el, this.transform.code)};
              }
              this.values.push(el);
            });
            this.setInitialSelection(false);
            this.loadMoreRows(true);
          }
        }
        this.loading = false;
      });
  }

  public rowTrackBy() {
    return function (index, row) {
      return get(row, 'id', index);
    };
  }

  // event.first = Index of the first record in this page
  // event.rows = Number of rows to display in new page
  // event.page = Index of the new page
  // event.pageCount = Total number of pages
  public paginate(event) {
    if (event) {
      this.pageChangeEvent = {...event};
      this.iterationPage = this.pageChangeEvent.page;

      if (this.searchText) {
        const searchTxt = this.searchText.toLowerCase();
        this.list = this.selectedValues.slice(event.first, (event.rows + event.first));
      } else {
        this.list = this.values.slice(event.first, (event.rows + event.first));
      }
    } else {
      this.list = this.values.slice(this.iterationPage, this.iterationRows);
    }

    if (this.selectedPageNumbers.indexOf(this.iterationPage, 0) >= 0) {
      this.updateTableCheckboxState(true);
      this.list.forEach(itemFromPage => {
        if (!this.selectedRows.find(item => item.id === itemFromPage.id)) {
          this.selectedRows.push(itemFromPage);
        }
      });
    } else {
      this.updateTableCheckboxState(false);
      this.list.forEach(itemFromPage => {
        this.selectedRows.filter(item => item.id === itemFromPage.id);
      });
    }
  }

  private loadMoreRows(paginate: boolean = false) {
    if (this.values && this.values.length > 0) {
      this.list = this.values.slice(this.first, this.first + this.iterationRows);
    } else if (!(this.values && this.values.length > 0)) {
      this.list = [];
    } else {
      this.list = [...this.list];
    }
    this.changeDetector.markForCheck();
    this.length = this.values.length;
  }

  public getPagingInfo(): string {
    const from = this.length < 1 ? 0 : this.pageChangeEvent ? this.pageChangeEvent.first + 1 : 1;
    const to = this.length > 0 ? ((from + this.iterationRows - 1) <= this.length ? (from + this.iterationRows - 1) : this.length) : 0;
    const total = this.length ? this.length : 0;
    return 'Showing ' + from + ' - ' + to + ' of ' + total;
  }

  private setInitialSelection(checkboxChecked: boolean) {

    // toggle header checkbox to unselected
    this.headerCheckedBoxState = checkboxChecked;
    this.updateTableCheckboxState(checkboxChecked);

    if (!this.keepExistedSelection) {
      this.selectedPageNumbers = []; // clean array of selected pages
      if(this.initState){
        this.initState = false;
        const initiallySelected = this.payload.alreadySelected && this.payload.alreadySelected.length > 0 ? this.payload.alreadySelected : [];
        let allSelectedRows = this.values.filter(el => initiallySelected
          .some(res => get(el, this.transform.display, '').toLowerCase() === get(res, this.transform.display, '').toLowerCase()));
        // combine with already selected
        allSelectedRows = unionWith(allSelectedRows, this.selectedRows, (el1, el2) =>
          get(el1, this.transform.display, '') === get(el2, this.transform.display, '')
        );
        this.selectedRows = this.values
          .filter(el => allSelectedRows
            .some(res => get(el, this.transform.display, '').toLowerCase() === get(res, this.transform.display, '').toLowerCase()));
        this.changeDetector.markForCheck();
      }
      else {
        let currentlySelected = cloneDeep(this.selectedRows);
        this.selectedRows = this.values
          .filter(el => currentlySelected
            .some(res => get(el, this.transform.display, '').toLowerCase() === get(res, this.transform.display, '').toLowerCase()));
        this.changeDetector.markForCheck();
      }

    }
  }

  private updateTableCheckboxState(checkboxChecked: boolean) {
    this.checkbox.checked = checkboxChecked;
    this.checkbox.cd.detectChanges();
  }

  public getSortColumn(item, sortKeys, i) {
    let column = null;
    if (item.sortable && Array.isArray(sortKeys)) {
      column = sortKeys[i];
    }
    return column;
  }

  public getColumnData(tableDescr, key, rowData): string {
    if (tableDescr && key && rowData) {
      return get(rowData, key.key, '');
    }
    return '';
  }

  public getItemType(item: any, key: any): string {
    const foundElement = item.keys.find(keyDescr => key === keyDescr);
    return foundElement ? foundElement.type : 'text';
  }

  onHeaderBoxClick($event) {
    const page = this.iterationPage;
    this.setSelectedPage($event.checked, page);
  }

  private setSelectedPage(checked: boolean, page: number) {
    this.updateTableCheckboxState(checked);
    if (checked === false) {
      this.selectedPageNumbers = this.selectedPageNumbers.filter((value, index) => value !== page);
    } else {
      this.selectedPageNumbers.pushIfNotExist(page, (a) => a === page);
    }
  }
}
